import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { ClipboardList, Factory, Plus } from "lucide-react";
import { useSetPageHeader } from "@/lib/page-header";
import { cn } from "@/lib/cn";
import { useAuth } from "@/lib/auth";
import { trpc } from "@/lib/trpc";
import { ProsthesisOrdersTable } from "@/components/prosthesis/ProsthesisOrdersTable";
import { ProsthesisLabsTab, type ConnectionFilter } from "@/components/prosthesis/ProsthesisLabsTab";
import { ProsthesisLabAddDialog } from "@/components/prosthesis/ProsthesisLabAddDialog";
import { useFluxoNovoPedido } from "@/components/prosthesis/FluxoNovoPedido";
import { ProsthesisOrderDetailDialog } from "@/components/prosthesis/ProsthesisOrderDetailDialog";
import { useParametroDaUrl } from "@/lib/use-parametro-da-url";
import { ProtesePorDentista } from "@/components/prosthesis/ProtesePorDentista";
import { usePermissao } from "@/lib/use-permissao";
import { useTourDaPagina } from "@/components/product-tour/PageTourEntry";
import { PROTESE_TOUR_STEPS } from "@/lib/product-tour/page-tour-steps";
import { FilterBar } from "@/components/shared/FilterBar";
import { BotaoLente } from "@/components/prosthesis/BotaoLente";
import { useIsMobile } from "@/lib/use-media-query";

type TabKey = "pedidos" | "parceiros";

/** Dentro de Pedidos: o mesmo conjunto de pedidos, lido de dois jeitos. Não são entidades
 *  diferentes — por isso vivem aqui e não na barra de cima, onde estão Pedidos e Parceiros, que
 *  são coisas distintas. */
type LenteKey = "todos" | "profissional";

const TABS: Array<{ key: TabKey; label: string; icon: typeof ClipboardList }> = [
  { key: "pedidos", label: "Pedidos", icon: ClipboardList },
  { key: "parceiros", label: "Parceiros", icon: Factory },
];

export function Protese() {
  const { user } = useAuth();
  const isPersonalOrg = !!user?.activeOrganization?.isPersonal;
  // Acesso pessoal de quem está em outras organizações: Pedidos junta todas, com filtro (como
  // Pacientes) — no lugar da antiga aba "Outras organizações".
  const minhas = trpc.organization.listMine.useQuery(undefined, { enabled: isPersonalOrg, staleTime: 60_000 });
  const deTodas = isPersonalOrg && (minhas.data ?? []).some((m) => !m.organization.isPersonal);
  const podeVerTodosOsPedidos = usePermissao("prosthesis.viewAll");

  const [lente, setLente] = useState<LenteKey>("todos");
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<TabKey>("pedidos");
  const [connectionFilter, setConnectionFilter] = useState<ConnectionFilter>("");
  const [newPartnerOpen, setNewPartnerOpen] = useState(false);
  const fluxo = useFluxoNovoPedido({ onConcluido: () => setTab("pedidos") });
  const [pedidoAberto, setPedidoAberto] = useState<string | null>(null);
  useParametroDaUrl("abrir", (id) => {
    setTab("pedidos");
    setPedidoAberto(id);
  });

  useTourDaPagina(PROTESE_TOUR_STEPS, () => setTab("pedidos"));
  useSetPageHeader(
    {
      title: "Prótese",
      subtitle: "Acompanhe os pedidos solicitados e gerencie laboratórios parceiros",
    },
    [],
  );

  useEffect(() => {
    if (searchParams.get("novo") === "1") {
      fluxo.abrirInicio();
      setSearchParams((prev) => {
        prev.delete("novo");
        return prev;
      }, { replace: true });
    }
    const tabParam = searchParams.get("tab");
    const conexaoParam = searchParams.get("conexao");
    if (tabParam === "parceiros" || conexaoParam) {
      if (tabParam === "parceiros") setTab("parceiros");
      if (conexaoParam === "conectado" || conexaoParam === "pendente") setConnectionFilter(conexaoParam);
      setSearchParams((prev) => {
        prev.delete("tab");
        prev.delete("conexao");
        return prev;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const goToNewOrder = fluxo.abrirNovo;

  return (
    <div className="mx-auto w-full max-w-[1600px]">
      {/* No celular a segmentação toma a linha inteira e o botão desce pra linha de baixo, também
          em largura cheia — em vez de os dois se espremerem lado a lado. No desktop seguem juntos. */}
      <FilterBar
        className="mb-5"
        quebra="sm"
        segmentacao={
          <div data-tour="protese-tabs" className="flex w-full items-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-1 sm:inline-flex sm:w-auto dark:border-border dark:bg-muted/40">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={cn(
                  "inline-flex h-11 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-[13px] font-semibold transition-colors sm:h-9 sm:flex-none sm:px-4",
                  active
                    ? "bg-brand-600 text-white shadow-sm"
                    : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
                )}
              >
                <t.icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
          </div>
        }
        acoes={
          // No celular o "Novo pedido" fica também no "Por dentista" — a lente virou um botão
          // pequeno ao lado da busca e a ação não pode sumir junto.
          tab === "pedidos" && (lente === "todos" || isMobile) ? (
            <button
              type="button"
              data-tour="protese-novo-pedido"
              onClick={goToNewOrder}
              className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 sm:h-9 sm:w-auto sm:px-4"
            >
              <Plus className="h-4 w-4" />
              Novo pedido
            </button>
          ) : tab === "parceiros" ? (
            <button
              type="button"
              onClick={() => setNewPartnerOpen(true)}
              className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 sm:h-9 sm:w-auto sm:px-4"
            >
              <Plus className="h-4 w-4" />
              Novo parceiro
            </button>
          ) : null
        }
      />

      {tab === "pedidos" && (
        <>
          {podeVerTodosOsPedidos && !isMobile && (
            <div className="mb-4 inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white p-0.5 dark:border-border dark:bg-card">
              {(
                [
                  { key: "todos", label: "Todos os pedidos" },
                  { key: "profissional", label: "Por dentista" },
                ] as Array<{ key: LenteKey; label: string }>
              ).map((l) => (
                <button
                  key={l.key}
                  type="button"
                  onClick={() => setLente(l.key)}
                  aria-pressed={lente === l.key}
                  className={cn(
                    "inline-flex h-9 items-center rounded-md px-3 text-[13px] font-medium transition-colors",
                    lente === l.key
                      ? "bg-brand-600 font-semibold text-white shadow-sm"
                      : "text-zinc-600 hover:text-zinc-900 dark:text-muted-foreground dark:hover:text-foreground",
                  )}
                >
                  {l.label}
                </button>
              ))}
            </div>
          )}
          {lente === "todos" ? (
            <ProsthesisOrdersTable
              deTodasAsOrganizacoes={deTodas}
              onRequest={goToNewOrder}
              onEditar={fluxo.editar}
              lente={podeVerTodosOsPedidos && isMobile ? <BotaoLente lente={lente} onChange={setLente} /> : undefined}
            />
          ) : (
            <ProtesePorDentista acao={isMobile ? <BotaoLente lente={lente} onChange={setLente} /> : undefined} />
          )}
        </>
      )}
      {tab === "parceiros" && (
        <ProsthesisLabsTab
          onAddNew={() => setNewPartnerOpen(true)}
          connectionFilter={connectionFilter}
          onConnectionFilterChange={setConnectionFilter}
        />
      )}

      {fluxo.elementos}
      <ProsthesisOrderDetailDialog orderId={pedidoAberto} onClose={() => setPedidoAberto(null)} onEditar={fluxo.editar} />

      <ProsthesisLabAddDialog open={newPartnerOpen} onClose={() => setNewPartnerOpen(false)} />
    </div>
  );
}
